import { Editor, Path, Range, ReactEditor, Transforms } from '@lumapps/wrex/slate';
import { focusAt } from '@lumapps/wrex/slate/utils/focusAt';

import type { TableEditor, TRElement } from '../types';
import { createTD } from './createTD';
import { createTH } from './createTH';
import { getCurrentRowsInTable } from './getCurrentRowsInTable';
import { getCurrentSelectionInfos } from './getCurrentSelectionInfos';
import { isTableHeaderCell } from './isTableHeaderCell';

interface AddColumnOptions {
    at?: Path;
    preventFocus?: boolean;
}

export const addColumn = (
    editor: ReactEditor & TableEditor,
    position: 'before' | 'after',
    options?: AddColumnOptions,
) => {
    /* get selection details (null if no selection or if selection not in table) */
    const selectionInfos = getCurrentSelectionInfos(editor);

    if (!selectionInfos) {
        return undefined;
    }

    const { isSelectionInOneCell, startCol, endCol } = selectionInfos;

    /* column to insert the first cell */
    const getInsertionCol = () => {
        if (position === 'before') {
            return startCol;
        }
        return isSelectionInOneCell ? startCol + 1 : endCol + 1;
    };

    const tablePath = [(options?.at || Range.start(editor.selection as Range).path)[0]];
    /* get the rows in current table */
    const { rowsInTable } = getCurrentRowsInTable(editor, tablePath);

    /* we skip the normalization here to avoid cells insertion fixes during the column insertion processus */
    Editor.withoutNormalizing(editor, () => {
        /* insert a new cell in each row */
        rowsInTable.forEach(([rowNode], index) => {
            /* in a column-header row, the new cell is also a column header; otherwise it is a plain data cell */
            const firstCellInRow = (rowNode as TRElement).children?.[0];
            const isColumnHeaderRow = isTableHeaderCell(firstCellInRow) && firstCellInRow.headerType === 'col';
            /* define the position to insert each cell (before or after the current position in table) */
            const pathToInsert = [...tablePath, index, getInsertionCol()];
            /* insert the new cell in each row */
            Transforms.insertNodes(editor, isColumnHeaderRow ? createTH(undefined, 'col') : createTD(), {
                at: pathToInsert,
            });
        });
    });

    /* focus on the first cell of the new column */
    const newCellPosition = {
        anchor: {
            path: [...tablePath, 0, getInsertionCol(), 0, 0],
            offset: 0,
        },
        focus: {
            path: [...tablePath, 0, getInsertionCol(), 0, 0],
            offset: 0,
        },
    };

    // For some reason, the focusAt doesn't work properly in the table context if we do not delay it.
    if (!options?.preventFocus) {
        setTimeout(() => {
            focusAt(editor, newCellPosition);
        }, 1);
    }

    return newCellPosition;
};
